/**
 * @module builder
 */
import type { OptionsDocument, QuoteDocument, TieredPackageTierId } from 'paintscout'
import { swapTierProducts } from './swap-tier-products'
// Deep import rather than the '../util' barrel, which re-exports calculate.ts and closes a cycle
// back into builder — the same hazard get-tier-prices documents.
import { calculateQuote } from '../util/calculate/calculate-quote'

/**
 * Apply a tier's product substitutions to the quote, then recalculate.
 *
 * The swap itself lives in swap-tier-products, which imports no calculator — `calculateQuote` runs
 * it on every pass, so it cannot be allowed to import back. This module is the swap plus the
 * recalculation, and is what callers outside the calculator use.
 *
 * `fromTierId` is no longer needed to decide what changes — each slot resolves from the product it
 * held on the base tier — but it is kept in the signature because callers pass it and because a
 * same-tier move is still a no-op.
 */
export function applyTierProducts(args: {
  quote: QuoteDocument
  options: OptionsDocument
  fromTierId: TieredPackageTierId
  toTierId: TieredPackageTierId
  calculate?: boolean
}): QuoteDocument {
  const { options, fromTierId, toTierId, calculate = true } = args

  if (fromTierId === toTierId) {
    return args.quote
  }

  const swapped = swapTierProducts({ quote: args.quote, options, toTierId })

  if (!swapped) {
    return args.quote
  }

  /**
   * The quote's products are this tier's products now, so record the tier before calculating.
   * `calculateQuote` re-applies the selected tier to every product slot, and a `selectedTierId`
   * still naming the tier we just left would make it swap everything straight back.
   *
   * Callers set the same field again afterwards (setSelectedTier, setTieredPackages); doing it here
   * as well keeps the intermediate quote honest, which matters because withResolvedTierPrices
   * prices tiers from exactly such an intermediate.
   */
  const quote: QuoteDocument = swapped.tieredPackages
    ? { ...swapped, tieredPackages: { ...swapped.tieredPackages, selectedTierId: toTierId } }
    : swapped

  return calculate ? calculateQuote({ quote, options }) : quote
}
